import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { UserProfile, User } from '../../../shared/models/auth.model';
import { RoutingSlipService } from '../../../shared/services/routing-slip.service';
import { LettersService } from '../../../shared/services/letters.service';
import { AuthService } from '../../../core/services/auth.service';
import { DocumentViewerModalComponent } from '../../../shared/components/ui/document-viewer-modal/document-viewer-modal.component';
import { DocumentService } from '../../../shared/services/document.service';
import {
  DocumentAttachment,
  UserAssignment,
} from '../../../shared/models/routing-slip.model';

@Component({
  selector: 'app-routing-slip-create',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    DatePickerModule,
    SelectModule,
    ToastModule,
    DocumentViewerModalComponent,
  ],
  providers: [
    RoutingSlipService,
    LettersService,
    MessageService,
    AuthService,
    DocumentService,
  ],
  templateUrl: './routing-slip-create.component.html',
  styleUrl: './routing-slip-create.component.scss',
})
export class RoutingSlipCreateComponent implements OnInit {
  // Current user
  currentUser: User | null = null;

  // User profile data
  userProfile: UserProfile | null = null;

  // Form properties
  step1Form!: FormGroup;
  step2Form!: FormGroup;
  currentStep: number = 1;
  totalSteps: number = 2;

  // Step 2 form properties - levels array, each level has permission + users
  levelsArray!: FormArray;

  // Options for dropdowns
  userOptions: { label: string; value: any }[] = [
    { label: 'Select User', value: null },
  ];

  // Loading state for users
  isLoadingUsers: boolean = false;

  permissionOptions: { label: string; value: any }[] = [
    // { label: 'Select Permission', value: null },
  ];

  // Loading state for permissions
  isLoadingPermissions: boolean = false;

  // File upload properties
  uploadedFiles: any[] = [];
  maxFileSize: number = 10000000; // 10MB
  isDragOver: boolean = false;

  // Loading state
  loading: boolean = false;

  // Date properties
  minDate: Date = new Date();

  // Document viewer modal properties
  showDocumentViewer: boolean = false;
  currentDocument: DocumentAttachment | null = null;

  // Document details popover (step 2 header)
  showDocumentDetailsPopover: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private routingSlipService: RoutingSlipService,
    private lettersService: LettersService,
    private messageService: MessageService,
    private authService: AuthService,
    private documentService: DocumentService,
  ) {}

  ngOnInit(): void {
    this.initializeForms();
    this.initializeUserProfile();
    this.loadAssignableUsers();
    this.loadPermissions();
  }

  private initializeUserProfile(): void {
    this.currentUser = this.authService.getCurrentUser();
    if (this.currentUser) {
      this.userProfile = {
        id: this.currentUser.email,
        name: this.currentUser.name,
        email: this.currentUser.email,
        role: this.currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }
  }

  private initializeForms(): void {
    // Step 1 Form - Document Upload
    this.step1Form = this.fb.group({
      documentName: ['', [Validators.required]],
      dueDate: ['', [Validators.required]],
      uploadedFile: [null, Validators.required],
    });

    // Step 2 Form - Levels (each level has permission + users array)
    this.levelsArray = this.fb.array([]);
    this.step2Form = this.fb.group({
      levels: this.levelsArray,
    });

    // Add initial level with one user slot
    this.addLevel();
  }

  /**
   * Load assignable users from the API
   */
  private loadAssignableUsers(): void {
    this.isLoadingUsers = true;

    this.lettersService.getAssignableUsers().subscribe({
      next: (users: any) => {
        // Transform the API response to match the expected format
        const transformedUsers = users.map((user: any) => ({
          label:
            user.name ||
            user.fullName ||
            `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          value: user.id,
        }));

        // Update userOptions with API data
        this.userOptions = [
          // { label: 'Select User', value: null },
          ...transformedUsers,
        ];

        this.isLoadingUsers = false;
      },
      error: (error: any) => {
        this.isLoadingUsers = false;

        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load assignable users',
        });
      },
    });
  }

  /**
   * Format permission name to add proper spacing
   */
  private formatPermissionName(name: string): string {
    // Handle common permission name patterns
    if (name === 'ViewOnly') return 'View Only';
    if (name === 'Review') return 'Review';
    if (name === 'ApproveReject') return 'Approve/Reject';
    if (name === 'FullAccess') return 'Full Access';

    // Generic formatting for camelCase or PascalCase
    return name
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim(); // Remove leading space
  }

  /**
   * Load permissions from the API
   */
  private loadPermissions(): void {
    this.isLoadingPermissions = true;

    this.routingSlipService.getPermissions().subscribe({
      next: (permissions: any) => {
        // Transform the API response to match the expected format
        const transformedPermissions = permissions.map((permission: any) => ({
          label: this.formatPermissionName(permission.name),
          value: permission.id,
        }));

        // Update permissionOptions with API data
        this.permissionOptions = [
          // { label: 'Select Permission', value: null },
          ...transformedPermissions,
        ];

        this.isLoadingPermissions = false;
      },
      error: (error: any) => {
        this.isLoadingPermissions = false;

        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load permissions',
        });
      },
    });
  }

  // File upload handlers
  onFileSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      if (file.size > this.maxFileSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'File Too Large',
          detail: 'File size must be less than 10MB',
        });
        return;
      }

      this.uploadedFiles = [file];
      this.step1Form.patchValue({
        uploadedFile: file,
      });

      this.messageService.add({
        severity: 'success',
        summary: 'File Selected',
        detail: `${file.name} has been selected`,
      });
    }
  }

  onFileRemove(): void {
    this.uploadedFiles = [];
    this.step1Form.patchValue({
      uploadedFile: null,
    });
    // Mark the field as touched to show validation error after removal
    this.step1Form.get('uploadedFile')?.markAsTouched();
  }

  // New file handling methods for native file input
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (file.size > this.maxFileSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'File Too Large',
          detail: 'File size must be less than 10MB',
        });
        return;
      }

      this.uploadedFiles = [file];
      this.step1Form.patchValue({
        uploadedFile: file,
      });

      // Mark the field as touched to show validation state
      this.step1Form.get('uploadedFile')?.markAsTouched();

      this.messageService.add({
        severity: 'success',
        summary: 'File Selected',
        detail: `${file.name} has been selected`,
      });
    }
  }

  // Drag and drop handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > this.maxFileSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'File Too Large',
          detail: 'File size must be less than 10MB',
        });
        return;
      }

      this.uploadedFiles = [file];
      this.step1Form.patchValue({
        uploadedFile: file,
      });

      // Mark the field as touched to show validation state
      this.step1Form.get('uploadedFile')?.markAsTouched();

      this.messageService.add({
        severity: 'success',
        summary: 'File Selected',
        detail: `${file.name} has been selected`,
      });
    }
  }

  // Navigation methods
  nextStep(): void {
    if (this.currentStep === 1) {
      // Validate all fields before proceeding
      if (this.step1Form.valid) {
        this.currentStep = 2;
      } else {
        // Mark all fields as touched to show validation errors
        this.markFormGroupTouched(this.step1Form);

        // Check which fields are missing and show specific error messages
        const documentName = this.step1Form.get('documentName')?.value;
        const dueDate = this.step1Form.get('dueDate')?.value;
        const uploadedFile = this.step1Form.get('uploadedFile')?.value;

        const missingFields: string[] = [];
        if (!documentName || documentName.trim() === '') {
          missingFields.push('Document Name');
        }
        if (!dueDate) {
          missingFields.push('Due Date');
        }
        if (!uploadedFile) {
          missingFields.push('Document Upload');
        }
        console.log('Missing fields:', missingFields);

        // Show toast message with specific missing fields
        const errorMessage =
          missingFields.length > 0
            ? `Please fill in all required fields: ${missingFields.join(', ')}`
            : 'Please fill in all required fields';

        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: errorMessage,
          life: 5000,
        });
      }
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  cancel(): void {
    this.router.navigate(['/routing-slip']);
  }

  // Form submission
  async submitForm(): Promise<void> {
    if (this.step2Form.valid) {
      this.loading = true;

      try {
        // Step 1: Create the routing slip document
        const documentName = this.step1Form.get('documentName')?.value;
        const dueDate = this.step1Form.get('dueDate')?.value;
        const uploadedFile = this.step1Form.get('uploadedFile')?.value;

        if (!documentName || !dueDate || !uploadedFile) {
          throw new Error('Missing required fields for document creation');
        }

        // Format due date as ISO string
        const formattedDueDate = new Date(dueDate).toISOString();

        // Create the routing slip document first
        const routingSlipResponse = await this.routingSlipService
          .createRoutingSlipDocument(
            documentName,
            formattedDueDate,
            uploadedFile,
          )
          .toPromise();

        // Extract the routing slip ID from the response
        const routingSlipId = routingSlipResponse?.id;

        if (!routingSlipId) {
          throw new Error('No routing slip ID returned from document creation');
        }

        // Step 2: Assign users to the routing slip
        const assignments = this.prepareAssignmentsData();

        if (assignments.length > 0) {
          await this.routingSlipService
            .assignUsersToRoutingSlip(routingSlipId, assignments)
            .toPromise();
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Routing slip created and users assigned successfully',
        });

        // Navigate back to routing slip list
        setTimeout(() => {
          this.router.navigate(['/routing-slip']);
        }, 1500);
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create routing slip. Please try again.',
        });
      } finally {
        this.loading = false;
      }
    } else {
      this.markFormGroupTouched(this.step2Form);
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
      });
    }
  }

  /**
   * Prepare assignments data for the API (from levels -> users, global stepOrder)
   */
  private prepareAssignmentsData(): UserAssignment[] {
    const assignments: UserAssignment[] = [];
    let stepOrder = 1;

    // `levelIndex` is 0-based; API expects `level` starting from 1.
    this.levelsFormArray.controls.forEach((levelGroup, levelIndex) => {
      const permission = levelGroup.get('permission')?.value;
      const usersArray = levelGroup.get('users') as FormArray;
      if (!usersArray) return;

      usersArray.controls.forEach((userGroup) => {
        const userId = userGroup.get('userId')?.value;
        if (userId && permission) {
          assignments.push({
            assignedUserId: userId.toString(),
            level: levelIndex + 1,
            permission: parseInt(permission),
            stepOrder: stepOrder++,
          });
        }
      });
    });

    return assignments;
  }

  // Helper method to mark all form controls as touched
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      // If it's a FormArray, mark all nested controls as touched
      if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      } else if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Helper method to check if a field has errors
  hasFieldError(form: FormGroup, fieldName: string): boolean {
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty));
  }

  // Helper method to get field error message
  getFieldError(form: FormGroup, fieldName: string): string {
    const field = form.get(fieldName);
    if (!field || !field.errors || (!field.touched && !field.dirty)) {
      return '';
    }

    const errors = field.errors;

    if (errors['required']) {
      return this.getRequiredErrorMessage(fieldName);
    }
    if (errors['minlength']) {
      return `${this.getFieldLabel(fieldName)} must be at least ${
        errors['minlength'].requiredLength
      } characters long.`;
    }
    if (errors['maxlength']) {
      return `${this.getFieldLabel(fieldName)} must not exceed ${
        errors['maxlength'].requiredLength
      } characters.`;
    }
    if (errors['pattern']) {
      return `${this.getFieldLabel(
        fieldName,
      )} contains invalid characters. Only letters, numbers, spaces, and basic punctuation are allowed.`;
    }
    if (errors['pastDate']) {
      return 'Due date must be today or in the future.';
    }

    return `${this.getFieldLabel(fieldName)} is invalid.`;
  }

  // Helper method to get required error message
  private getRequiredErrorMessage(fieldName: string): string {
    const fieldLabels: { [key: string]: string } = {
      documentName: 'Document name is required',
      dueDate: 'Due date is required',
      uploadedFile: 'Please select a file to upload',
      userId: 'Please select a user',
      permission: 'Please select a permission level',
    };
    return (
      fieldLabels[fieldName] || `${this.getFieldLabel(fieldName)} is required`
    );
  }

  // Helper method to get field label
  private getFieldLabel(fieldName: string): string {
    const fieldLabels: { [key: string]: string } = {
      documentName: 'Document name',
      dueDate: 'Due date',
      uploadedFile: 'File upload',
      userId: 'User',
      permission: 'Permission',
    };
    return fieldLabels[fieldName] || fieldName;
  }

  // Levels FormArray methods
  addLevel(): void {
    const usersInLevel = this.fb.array([this.createUserControl()]);
    const levelGroup = this.fb.group({
      permission: ['', Validators.required],
      users: usersInLevel,
    });
    this.levelsArray.push(levelGroup);
  }

  removeLevel(levelIndex: number): void {
    if (this.levelsArray.length > 1) {
      this.levelsArray.removeAt(levelIndex);
    }
  }

  private createUserControl(): FormGroup {
    return this.fb.group({
      userId: ['', Validators.required],
    });
  }

  addUserToLevel(levelIndex: number): void {
    const levelGroup = this.levelsArray.at(levelIndex) as FormGroup;
    const usersArray = levelGroup.get('users') as FormArray;
    usersArray.push(this.createUserControl());
  }

  removeUserFromLevel(levelIndex: number, userIndex: number): void {
    const levelGroup = this.levelsArray.at(levelIndex) as FormGroup;
    const usersArray = levelGroup.get('users') as FormArray;
    if (usersArray.length > 1) {
      usersArray.removeAt(userIndex);
    }
  }

  // Getter methods for form validation
  get isStep1Valid(): boolean {
    return this.step1Form.valid;
  }

  get isStep2Valid(): boolean {
    return this.step2Form.valid;
  }

  get canProceedToStep2(): boolean {
    if (this.currentStep !== 1) return false;

    // Check if all required fields are filled: document name, due date, and uploaded file
    const documentName = this.step1Form.get('documentName')?.value;
    const dueDate = this.step1Form.get('dueDate')?.value;
    const uploadedFile = this.step1Form.get('uploadedFile')?.value;

    return !!(
      documentName &&
      dueDate &&
      uploadedFile &&
      documentName.trim() !== '' &&
      dueDate
    );
  }

  get canSubmit(): boolean {
    return this.currentStep === 2 && this.step2Form.valid;
  }

  get levelsFormArray(): FormArray {
    return this.step2Form.get('levels') as FormArray;
  }

  /**
   * Get filtered user options for a specific user dropdown in a level.
   * Excludes users already selected in any level (no re-assignment).
   */
  getFilteredUserOptions(
    levelIndex: number,
    userIndex: number,
  ): { label: string; value: any }[] {
    const selectedUserIds = new Set<any>();

    this.levelsFormArray.controls.forEach((levelGroup, li) => {
      const usersArray = (levelGroup as FormGroup).get('users') as FormArray;
      if (!usersArray) return;
      usersArray.controls.forEach((userGroup, ui) => {
        if (li !== levelIndex || ui !== userIndex) {
          const userId = userGroup.get('userId')?.value;
          if (userId) selectedUserIds.add(userId);
        }
      });
    });

    const currentControl = (
      this.levelsFormArray.at(levelIndex) as FormGroup
    ).get('users') as FormArray;
    const currentUserId = currentControl?.at(userIndex)?.get('userId')?.value;

    return this.userOptions.filter((option) => {
      if (option.value === currentUserId) return true;
      return !selectedUserIds.has(option.value);
    });
  }

  /**
   * Get the users FormArray for a given level index
   */
  getLevelUsers(levelIndex: number): FormArray {
    const levelGroup = this.levelsFormArray.at(levelIndex) as FormGroup;
    return levelGroup?.get('users') as FormArray;
  }

  /**
   * Get permission label by value for display in level header
   */
  getPermissionLabel(permissionValue: any): string {
    if (permissionValue == null || permissionValue === '') return '';
    const opt = this.permissionOptions.find(
      (o) =>
        o.value === permissionValue ||
        o.value === parseInt(permissionValue, 10),
    );
    return opt ? opt.label : '';
  }

  // Document viewer methods
  onViewDocument(): void {
    const uploadedFile = this.step1Form.get('uploadedFile')?.value;
    if (!uploadedFile) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No document available to view.',
      });
      return;
    }

    // Create a blob URL for viewing the local file
    const url = window.URL.createObjectURL(uploadedFile);

    // Create a document attachment object for the viewer
    this.currentDocument = {
      name: uploadedFile.name,
      size: this.formatFileSize(uploadedFile.size),
      type: uploadedFile.type || 'application/pdf',
      fileData: url, // Store the blob URL for viewing
      lastModified: uploadedFile.lastModified,
      blobUrl: url, // Store the blob URL separately for cleanup
      originalBlob: uploadedFile, // Store the original file for download
    };

    // Show the document viewer modal
    this.showDocumentViewer = true;

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Document loaded successfully.',
    });
  }

  onDocumentViewerClose(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.currentDocument?.blobUrl) {
      window.URL.revokeObjectURL(this.currentDocument.blobUrl);
    }
    this.showDocumentViewer = false;
    this.currentDocument = null;
  }

  onDocumentDownload(attachment: DocumentAttachment): void {
    // If we have the original file from the upload, use it directly
    if (attachment.originalBlob) {
      // Create download link
      const url = window.URL.createObjectURL(attachment.originalBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Document downloaded successfully.',
      });
    } else {
      // Fallback to local document service if no file available
      this.documentService.downloadDocument(attachment);
    }
  }

  onDocumentOpenInNewTab(attachment: DocumentAttachment): void {
    this.documentService.viewDocument(attachment);
  }

  /**
   * Format file size in human readable format
   * @param bytes File size in bytes
   * @returns Formatted file size string
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
